// What the reader is looking at: a workspace, a database it gives them, and
// the view that database is read through.
//
// The three are chosen in that order, each inside the one above it, and this
// module owns all three. Keeping them in three places let them disagree:
// Russell switched to the workspace "bpExperts Cloud" and the account menu
// still read "Internal library - View: All objects", a database that
// workspace does not give him.
//
// The workspace is the context, and changing it changes what you see without
// ceremony (product owner, 28 Sep 2026 - "like Signavio"):
//
//   * the database the reader had open **in that workspace** is reopened, so
//     switching back and forth lands where they left off. Nothing is
//     announced: choosing a workspace and arriving in its own library is the
//     outcome they asked for, not an incident.
//   * a workspace they have never opened opens its first database, silently.
//   * the app speaks only where something the reader actually asked for is
//     not available: a link into a database they cannot open, or a database
//     that went away while they were in it. Then it says so once.
//
// None of it widens anything. The server validates the `database` header
// against every database the person may use, in any of their workspaces
// (backend/controllers/bindVerifiedIdentity.js), so a remembered choice can
// neither grant a database nor lose one - it only decides which of them the
// screens offer and which one is open.

const WORKSPACE_KEY = "workspace";
const DATABASE_KEY = "db";
// The view in force right now, by id - the flat key the request headers read
// (actions/index.js getAuthHeaders), exactly as `db` is.
const VIEW_KEY = "view";
// Where the reader was in each workspace: { [workspaceId]: databaseKey }.
const PER_WORKSPACE_KEY = "bf.databaseByWorkspace";
// Which view the reader chose for each database: { [databaseKey]: viewId }.
// Keyed by database, not by workspace, because a view belongs to exactly one
// database (services/views.js) and a database to one workspace - so the
// database is the narrower key, and the workspace follows it. The same
// per-context memory as `bf.databaseByWorkspace`: a choice survives a reload
// and is checked against the profile that arrives after it.
const PER_DATABASE_VIEW_KEY = "bf.viewByDatabase";
// A sentence to say once, on the other side of the reload a change causes,
// and the last one said - so nothing can repeat it on a re-render.
const NOTE_KEY = "bf.contextNote";
const NOTE_SAID_KEY = "bf.contextNoteSaid";

const byName = (a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });

// A browser that refuses storage must never break a page: the choice simply
// lasts until the next load.
const read = (store, key) => {
  try {
    return store.getItem(key);
  } catch (err) {
    return null;
  }
};

const write = (store, key, value) => {
  try {
    if (value) store.setItem(key, value);
    else store.removeItem(key);
  } catch (err) {
    /* private window, blocked storage: the choice lasts this page only */
  }
};

// Database names travel through URLs and older stored values, where the case
// may differ from the server's. Compared without it, and the server's
// spelling is the one that is used.
const sameName = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();
const canonical = (names, wanted) => (wanted ? names.find((name) => sameName(name, wanted)) || null : null);

/* ------------------------------------------------------------------ *
 * 1. The workspace                                                    *
 * ------------------------------------------------------------------ */

// Every workspace the person belongs to, by name - the order the switcher
// shows and the fallback order when nothing is chosen.
export const workspacesOf = (user) => (user?.workspaces || []).filter((workspace) => workspace && workspace.id).slice().sort(byName);

export const hasSeveralWorkspaces = (user) => workspacesOf(user).length > 1;

// The chosen workspace, else the one the server opened in, else the first by
// name. A stored id for a workspace the person has since left is ignored.
export const currentWorkspace = (user) => {
  const all = workspacesOf(user);
  if (all.length === 0) return null;
  const id = read(window.localStorage, WORKSPACE_KEY);
  return all.find((workspace) => workspace.id === id) || all.find((workspace) => workspace.id === user?.workspace?.id) || all[0];
};

export const currentWorkspaceName = (user) => currentWorkspace(user)?.name || user?.workspace?.name || "";

export const rememberWorkspace = (id) => write(window.localStorage, WORKSPACE_KEY, id);

/* ------------------------------------------------------------------ *
 * 2. The database                                                     *
 * ------------------------------------------------------------------ */

export const rememberedDatabase = () => read(window.localStorage, DATABASE_KEY);

const perWorkspace = () => {
  try {
    const stored = JSON.parse(read(window.localStorage, PER_WORKSPACE_KEY) || "{}");
    return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
  } catch (err) {
    return {};
  }
};

// Where the reader was in one workspace, whether or not it is the one they
// are in now. Unknown until they have opened a database there.
export const databaseRememberedIn = (workspaceId) => (workspaceId ? perWorkspace()[workspaceId] || null : null);

// Remember the database in use, and - so switching workspaces lands where the
// reader left off - remember it as this workspace's too.
export const rememberDatabase = (name, workspaceId = read(window.localStorage, WORKSPACE_KEY)) => {
  write(window.localStorage, DATABASE_KEY, name);
  // The view in force follows the database: the one chosen for it, else none
  // - which is every view this person holds there, merged, as before.
  write(window.localStorage, VIEW_KEY, (name && perDatabaseView()[viewKeyOf(name)]) || "");
  if (!workspaceId) return;
  const all = perWorkspace();
  if (name) all[workspaceId] = name;
  else delete all[workspaceId];
  write(window.localStorage, PER_WORKSPACE_KEY, JSON.stringify(all));
};

// The databases one workspace gives this person: what it owns, narrowed to
// what the server allows. Always a subset, never more.
export const databasesOfWorkspace = (user, workspaceId) => {
  const allowed = Array.isArray(user?.databases) ? user.databases : [];
  const workspace = workspacesOf(user).find((one) => one.id === workspaceId);
  return (workspace?.databases || []).filter((name) => canonical(allowed, name)).map((name) => canonical(allowed, name));
};

// The databases the screens offer while this workspace is chosen:
//
//   * the ones it gives, first, and
//   * the ones this person may open that belong to none of their workspaces -
//     a personal library, and for bpExperts staff the server's own databases,
//     which are theirs to reach from wherever they are.
//
// A database of one of their *other* workspaces is deliberately absent: it is
// reached by switching to that workspace, which is what the switcher is for.
// With a single workspace nothing is taken away, so the list is unchanged.
export const workspaceDatabases = (user, workspaceId) => {
  const allowed = Array.isArray(user?.databases) ? user.databases : [];
  const all = workspacesOf(user);
  if (all.length === 0) return allowed;
  const chosen = workspaceId === undefined ? currentWorkspace(user)?.id || null : workspaceId;
  const mine = databasesOfWorkspace(user, chosen);
  const elsewhere = all.filter((workspace) => workspace.id !== chosen).flatMap((workspace) => workspace.databases || []);
  const unaffiliated = allowed.filter((name) => !canonical(mine, name) && !elsewhere.some((other) => sameName(other, name)));
  return [...mine, ...unaffiliated];
};

/* ------------------------------------------------------------------ *
 * 3. The view                                                         *
 * ------------------------------------------------------------------ */

// A view belongs to exactly one database and a person may hold several - two
// on the same database included (product owner, 28 Sep 2026, item 7: "if he
// has more than 1 VIEW: select View... at any time he can switch view on the
// fly"). So the switcher offers views, and the database comes with the one
// that is chosen.
//
// None of this widens anything. The id travels as the `view` header and the
// server uses it only where the person already holds that view for the
// database of the request (controllers/bindVerifiedIdentity.js): an unknown,
// stale or forged id is ignored and every view they hold for it is merged,
// which is what they saw before choosing. A choice can only narrow.

export const currentViewId = () => read(window.localStorage, VIEW_KEY) || "";

const perDatabaseView = () => {
  try {
    const stored = JSON.parse(read(window.localStorage, PER_DATABASE_VIEW_KEY) || "{}");
    return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
  } catch (err) {
    return {};
  }
};

const viewKeyOf = (database) => String(database || "").toLowerCase();

// Which view the reader chose for one database, whether or not it is open.
export const viewRememberedIn = (database) => (database ? perDatabaseView()[viewKeyOf(database)] || null : null);

// Remember the view chosen for a database, and - where that database is the
// one open - put it in force. An empty id forgets the choice, which is the
// merge of every view they hold there: what the server does unasked.
export const rememberView = (viewId, database) => {
  const all = perDatabaseView();
  if (database) {
    if (viewId) all[viewKeyOf(database)] = viewId;
    else delete all[viewKeyOf(database)];
    write(window.localStorage, PER_DATABASE_VIEW_KEY, JSON.stringify(all));
  }
  if (!database || sameName(database, rememberedDatabase())) write(window.localStorage, VIEW_KEY, viewId || "");
};

// The views this person holds in one workspace, each carrying the one
// database it was made for - and only while that workspace still offers them
// that database. The profile is the authority; a remembered id is not.
export const viewsOfWorkspace = (user, workspaceId) => {
  const workspace = workspacesOf(user).find((one) => one.id === workspaceId);
  const offered = workspaceDatabases(user, workspaceId);
  return (workspace?.views || [])
    .filter((view) => view && view.id && canonical(offered, view.database))
    .map((view) => ({ ...view, database: canonical(offered, view.database) }))
    // Grouped by library, then by name inside it: two views on one library
    // sit together, which is how a reader reads the list.
    .sort(
      (a, b) =>
        String(a.database).localeCompare(String(b.database), undefined, { sensitivity: "base" }) ||
        String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
    );
};

// The views that apply to one database, in the order the switcher lists them.
export const viewsForDatabase = (user, database, workspaceId) =>
  viewsOfWorkspace(user, workspaceId).filter((view) => sameName(view.database, database));

// What the switcher offers: views where this person holds any, and a plain
// database entry for each database of the workspace no view of theirs
// covers - staff and workspace admins, who read it unfiltered.
//
//   { key, viewId, viewName, database, mode }
//
// viewId null is the plain database entry. With no views at all the list is
// the databases, exactly as the bar has always offered them.
export const switcherOptions = (user, workspaceId = undefined) => {
  const chosen = workspaceId === undefined ? currentWorkspace(user)?.id || null : workspaceId;
  const databases = workspaceDatabases(user, chosen);
  const views = viewsOfWorkspace(user, chosen);
  const covered = new Set(views.map((view) => viewKeyOf(view.database)));
  const bare = databases
    .filter((name) => !covered.has(viewKeyOf(name)))
    .map((name) => ({ key: `db:${name}`, viewId: null, viewName: null, database: name, mode: null }));
  if (views.length === 0) return bare;
  return [
    ...views.map((view) => ({
      key: `view:${view.id}`,
      viewId: view.id,
      viewName: view.name || null,
      database: view.database,
      mode: view.mode === "edit" ? "edit" : "view",
    })),
    ...bare,
  ];
};

// Which of this person's workspaces gives a database - where a link names one
// they may open but are not currently standing in.
export const workspaceGiving = (user, database) =>
  database ? workspacesOf(user).find((workspace) => (workspace.databases || []).some((name) => sameName(name, database))) || null : null;

/* ------------------------------------------------------------------ *
 * The three together                                                  *
 * ------------------------------------------------------------------ */

const labelFor = (user, name) => (name ? user?.databaseLabels?.[name] || name : "");

// What the reader is looking at, resolved in one place and in one order.
//
//   workspace  the workspace a link asked for, where it names a database this
//              person may open and the one they are in does not give it;
//              else the one they chose, while they are still in it; else the
//              one the server opened in; else the first by name
//   database   the one the link asked for, while that workspace gives it;
//              else the one they last had open **in this workspace**; else
//              the one open now, while this workspace gives it; else their
//              main database; else this workspace's first
//   view       never chosen here: it follows the database
//
// `note` is the one thing to say, and it is empty for an ordinary switch.
// It is written only where the reader asked for something they did not get:
// a link into a database they cannot open, a link they could only be shown by
// moving workspace, or a database that went away while they were in it.
export const resolveContext = (user, { remembered = rememberedDatabase(), asked = null } = {}) => {
  const allowed = Array.isArray(user?.databases) ? user.databases : [];
  const askedFor = canonical(allowed, asked);
  const refused = Boolean(asked) && !askedFor;

  // A link into a database of another of this person's workspaces moves the
  // workspace to match, because that is the only way to show it to them.
  const here = currentWorkspace(user);
  const givesAsked = askedFor && canonical(workspaceDatabases(user, here ? here.id : undefined), askedFor);
  const elsewhere = askedFor && !givesAsked ? workspaceGiving(user, askedFor) : null;
  const workspace = elsewhere || here;

  const databases = workspaceDatabases(user, workspace ? workspace.id : undefined);
  const lastHere = databaseRememberedIn(workspace?.id);
  const database =
    canonical(databases, askedFor) ||
    canonical(databases, lastHere) ||
    canonical(databases, remembered) ||
    canonical(databases, user?.mainDatabase) ||
    databases[0] ||
    null;

  // The database that was open is not merely in another workspace - it is
  // gone from everything this person may use. A grant withdrawn, a database
  // archived: something they were using has been taken away, and that is
  // worth one sentence.
  const wasUsing = remembered && !canonical(allowed, remembered);

  let note = "";
  if (refused) {
    note = database
      ? `You can't open ${asked}. Opened ${labelFor(user, database)} instead.`
      : `You can't open ${asked}, and there is no other database for you to open.`;
  } else if (elsewhere) {
    note = `${labelFor(user, askedFor)} is in ${elsewhere.name}. Switched to that workspace.`;
  } else if (wasUsing) {
    note = database
      ? `${labelFor(user, remembered)} is no longer available to you. Opened ${labelFor(user, database)} instead.`
      : `${labelFor(user, remembered)} is no longer available to you.`;
  }

  // The view, last and narrowest: the one chosen for this database, while
  // the profile still says they hold it; else the only one they hold there;
  // else none - every view they hold for it, merged, which is what the
  // server does when the request names none.
  const viewsHere = viewsForDatabase(user, database, workspace?.id);
  const remembers = viewRememberedIn(database);
  const view = viewsHere.find((one) => one.id === remembers) || (viewsHere.length === 1 ? viewsHere[0] : null) || null;

  return {
    workspace,
    workspaceId: workspace?.id || null,
    workspaceName: workspace?.name || user?.workspace?.name || "",
    databases,
    database,
    view,
    viewId: view?.id || null,
    views: viewsHere,
    // A link asked for a database this person cannot open: the screens send
    // them somewhere they can, rather than to a page that will say "not
    // found" about an object in a database they never had.
    refused,
    movedWorkspace: Boolean(elsewhere) && elsewhere.id !== here?.id,
    moved: Boolean(database) && Boolean(remembered) && !sameName(database, remembered),
    empty: databases.length === 0,
    note,
  };
};

// The same, written down: what sign-in does once the profile has arrived, so
// a workspace or a database remembered from a membership that has since
// changed can never decide what opens. `asked` is the database a deep link
// named, where the reader followed one.
export const applyContext = (user, { asked = null } = {}) => {
  const context = resolveContext(user, { asked });
  if (context.workspaceId) rememberWorkspace(context.workspaceId);
  if (context.database) rememberDatabase(context.database, context.workspaceId);
  // A view the person no longer holds is dropped here rather than carried
  // into a request: the profile has just arrived and it is the authority.
  if (context.database) rememberView(context.viewId || "", context.database);
  if (context.note) noteContextChange(context.note);
  return context;
};

/* ------------------------------------------------------------------ *
 * Saying it once                                                      *
 * ------------------------------------------------------------------ */

// Tied to the act, never to a render: the same sentence is never said twice
// in a row, whatever reloads or re-renders happen in between.
export const noteContextChange = (text) => {
  if (!text) return;
  if (read(window.sessionStorage, NOTE_SAID_KEY) === text) return;
  write(window.sessionStorage, NOTE_KEY, text);
  write(window.sessionStorage, NOTE_SAID_KEY, text);
};

export const takeContextNote = () => {
  const note = read(window.sessionStorage, NOTE_KEY);
  if (note) write(window.sessionStorage, NOTE_KEY, "");
  return note || "";
};

/* ------------------------------------------------------------------ *
 * Changing one of them                                                *
 * ------------------------------------------------------------------ */

// Switch workspace, and take the database with it: the database the reader
// last had open in that workspace, else its first, else - where they may open
// nothing there - nothing, and the empty state says so. The one in use stays
// open where the workspace chosen also gives it.
//
// Silent by design. Arriving in the library of the workspace you just chose
// is the point of choosing it.
export const switchWorkspace = async (user, workspaceId, currentDatabase, history, switchDatabase) => {
  rememberWorkspace(workspaceId);
  const offered = workspaceDatabases(user, workspaceId);
  const lastThere = canonical(offered, databaseRememberedIn(workspaceId));
  const target = lastThere || (canonical(offered, currentDatabase) ? null : databasesOfWorkspace(user, workspaceId)[0] || offered[0] || null);
  if (!target) {
    // This workspace gives them nothing. The page they are on names the old
    // workspace's database in its address, so it is left behind first: the
    // empty state says where they are, rather than a link contradicting it.
    history.push("/home");
    history.go(0);
    return;
  }
  if (sameName(target, currentDatabase)) {
    // Already there: everything is re-read where it is.
    history.go(0);
    return;
  }
  await switchDatabase(target, history);
};

// Switch to one of the switcher's entries. A view carries its database, so
// choosing one may move the library too; choosing another view of the same
// library re-reads it under that view, where it stands.
//
// The choice is remembered against its database (`bf.viewByDatabase`) before
// anything moves, so the reload lands under the view that was chosen - and
// so it is still in force after the next reload.
export const switchView = async (user, option, currentDatabase, history, switchDatabase) => {
  if (!option || !option.database) return;
  rememberView(option.viewId || "", option.database);
  if (!sameName(option.database, currentDatabase)) {
    await switchDatabase(option.database, history);
    return;
  }
  // The library is already open: only the view changed. rememberDatabase
  // puts the new choice in force for the request headers, then everything is
  // read again under it.
  rememberDatabase(option.database);
  history.go(0);
};
